import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { get, postJson } from '../../../hook/request'; 
import { H3 } from '../../../AbstractElements';

type Tipo = 'Cluster'; 
type ApiItem = {
  ID: number;
  type: string;        
  campo: string;
  nome: string;
  ordine: number;
};

type RowItem = {
  __id: string;      
  campo: string;
  nome: string;
};

const TIPO_OPTIONS: { value: Tipo; label: string }[] = [
  { value: 'Cluster', label: 'Cluster' },
];

function uid() {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export default function TypeOrderForm() {
  const [tipo, setTipo] = useState<Tipo>('Cluster');
  const [rows, setRows] = useState<RowItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const canSave = useMemo(
    () =>
      rows.length > 0 &&
      rows.every(r => r.campo.trim() !== '' && r.nome.trim() !== ''),
    [rows]
  );

  // ===== Carica lista al cambio tipo =====
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await get<any>(`/typeOrder/${encodeURIComponent(tipo)}`);
        const payload = (res as any)?.data ?? res;
        const list: ApiItem[] = Array.isArray(payload?.list)
          ? payload.list
          : Array.isArray(payload)
          ? payload
          : [];

        list.sort((a, b) => (a.ordine ?? 0) - (b.ordine ?? 0));

        setRows(
          list.map(it => ({
            __id: uid(),
            campo: it.campo ?? '',
            nome: it.nome ?? '',
          }))
        );
      } catch (e: any) {
        toast.error(
          e?.response?.data?.message ?? e?.message ?? 'Errore nel caricamento'
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [tipo]);

  // ===== Handlers =====
  const onRowChange =
    (idx: number, key: keyof RowItem) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.currentTarget.value;
      setRows(prev => {
        const next = [...prev];
        next[idx] = { ...next[idx], [key]: val };
        return next;
      });
    };

  const addRow = () => {
    setRows(prev => [...prev, { __id: uid(), campo: '', nome: '' }]);
  };

  const removeRow = (idx: number) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    setRows(prev => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };

  const moveDown = (idx: number) => {
    if (idx === rows.length - 1) return;
    setRows(prev => {
      const next = [...prev];
      [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
      return next;
    });
  };

  // ===== Salva =====
  const onSave = async () => {
    if (!canSave) {
      toast.warn('Compila tutti i campi "Campo" e "Nome".');
      return;
    }
    const user_email = localStorage.getItem('email') || 'supporto@i-factory.biz';

    const payload = {
      tipo, // "Cluster"
      user_email,
      campi: rows.map((r, i) => ({
        campo: r.campo.trim(),
        nome: r.nome.trim(),
        ordine: i, // posizione corrente
      })),
    };

    try {
      setSaving(true);
      await postJson('/typeOrder/fields', payload);
      toast.success('Configurazione salvata con successo');
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ?? e?.message ?? 'Errore durante il salvataggio'
      );
    } finally {
      setSaving(false);
    }
  };

  // ===== UI =====
  return (
    <Container fluid className="pt-3">
      <Card>
        <CardBody>
          <Row className="g-3 align-items-end">
            <Col md={12}>
                <H3>Configurazione Campi</H3>
            </Col>
            <Col md={3}>
              <Label>Tipo</Label>
              <Input
                type="select"
                value={tipo}
                onChange={e => setTipo(e.currentTarget.value as Tipo)}
                disabled={loading || saving}
              >
                {TIPO_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Input>
            </Col>

            <Col className="d-flex justify-content-end">
              <Button color="success" outline onClick={addRow} disabled={loading || saving}>
                <i className="fa fa-plus me-2" />
                Aggiungi campo
              </Button>
            </Col>
          </Row>

          <hr className="my-3" />

          {loading ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner size="sm" /> Caricamento…
            </div>
          ) : (
            <>
              {rows.length === 0 ? (
                <div className="text-muted">Nessun campo. Aggiungine uno con il pulsante in alto.</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {rows.map((r, idx) => (
                    <Row key={r.__id} className="g-2 align-items-center w-100">
                      <Col md={4}>
                        <Label className="mb-1 d-block">Campo</Label>
                        <Input
                          value={r.campo}
                          onChange={onRowChange(idx, 'campo')}
                          placeholder="es. segm1_frequency"
                          disabled={saving}
                        />
                      </Col>
                      <Col md={5}>
                        <Label className="mb-1 d-block">Nome</Label>
                        <Input
                          value={r.nome}
                          onChange={onRowChange(idx, 'nome')}
                          placeholder="es. Frequency"
                          disabled={saving}
                        />
                      </Col>
                      <Col md={3} className="d-flex align-items-end gap-2">
                        <Button
                          color="light"
                          onClick={() => moveUp(idx)}
                          disabled={idx === 0 || saving}
                          title="Su"
                        >
                          ↑ Su
                        </Button>
                        <Button
                          color="light"
                          onClick={() => moveDown(idx)}
                          disabled={idx === rows.length - 1 || saving}
                          title="Giù"
                        >
                          ↓ Giù
                        </Button>
                        <Button
                          color="danger"
                          outline
                          onClick={() => removeRow(idx)}
                          disabled={saving}
                        >
                          <i className="fa fa-trash me-1" />
                          Rimuovi
                        </Button>
                      </Col>
                    </Row>
                  ))}
                </div>
              )}
            </>
          )}

          <div className="d-flex justify-content-end mt-4">
            <Button color="primary" onClick={onSave} disabled={!canSave || saving || loading}>
              {saving ? <Spinner size="sm" /> : 'Salva'}
            </Button>
          </div>
        </CardBody>
      </Card>
    </Container>
  );
}
